/**
 * Single source of truth for the primary navigation.
 *
 * Every entry resolves to a real destination: `to` for in-portal routes and
 * `href` for other government systems, which open in a new tab.
 */

export const primaryNavigation = [
    {
        id: 'about',
        label: 'About',
        to: '/profile',
        description:
            'How the Ministry is organised, who leads it, and how to reach the right office.',
        columns: [
            {
                title: 'The Ministry',
                links: [
                    { label: 'Overview and mandate', to: '/profile' },
                    { label: 'Directorates and offices', to: '/directorates' },
                    { label: 'Development partners', to: '/partners' },
                    { label: 'Former Ministers', to: '/formerministers' },
                ],
            },
            {
                title: 'Leadership',
                links: [
                    { label: 'Minister of Finance', to: '/pagedetails/minister-of-finance' },
                    { label: 'Organisational structure', to: '/organization-chart' },
                    { label: 'Programmes and reform', to: '/programs' },
                    { label: 'Research and analysis', to: '/research' },
                ],
            },
            {
                title: 'Accountability',
                links: [
                    { label: 'Publications and reports', to: '/publications' },
                    { label: 'Treasury documents', to: '/alltreasurydocuments' },
                    { label: 'Knowledge centre', to: '/allknowledge' },
                    { label: 'Infographics and posters', to: '/allpostergraphics' },
                ],
            },
            {
                title: 'Get in touch',
                links: [
                    { label: 'Contact the Ministry', to: '/contact' },
                    { label: 'Careers and vacancies', to: '/vacancies' },
                    { label: 'Search the portal', to: '/searchpage' },
                ],
            },
        ],
    },
    {
        id: 'budget',
        label: 'Budget & spending',
        to: '/budgetspendingpage',
        description:
            'The State Budget cycle, execution reporting and the revenue that funds public services.',
        columns: [
            {
                title: 'State Budget',
                links: [
                    { label: 'Budget and spending overview', to: '/budgetspendingpage' },
                    { label: 'Budget and revenue by year', to: '/budgetandrevenueallyears' },
                    { label: 'Treasury documents', to: '/alltreasurydocuments' },
                ],
            },
            {
                title: 'Revenue',
                links: [
                    { label: 'Revenue collections', to: '/allcustomrevenuecollections' },
                    { label: 'Tax Authority', href: 'https://attl.gov.tl/' },
                    { label: 'Customs Authority', href: 'https://customs.gov.tl/' },
                ],
            },
            {
                title: 'Transparency',
                links: [
                    { label: 'Publications and reports', to: '/publications' },
                    { label: 'Public consultations', to: '/consultations' },
                    { label: 'Budget infographics', to: '/allpostergraphics' },
                    { label: 'Open data and statistics', href: 'https://inetl-ip.gov.tl' },
                ],
            },
            {
                title: 'For suppliers',
                links: [
                    { label: 'Track an invoice', to: '/budgetspendingpage' },
                    { label: 'Open tenders', to: '/tenders' },
                    { label: 'e-Procurement Portal', href: 'http://www.eprocurement.gov.tl/' },
                ],
            },
        ],
    },
    {
        id: 'procurement',
        label: 'Procurement',
        to: '/procurementpage',
        description:
            'Tender opportunities, supplier registration and the rules that govern public purchasing.',
        columns: [
            {
                title: 'Opportunities',
                links: [
                    { label: 'Procurement overview', to: '/procurementpage' },
                    { label: 'Current tenders', to: '/tenders' },
                    { label: 'Tender archive', to: '/tenders_archive' },
                ],
            },
            {
                title: 'Suppliers',
                links: [
                    { label: 'e-Procurement Portal', href: 'http://www.eprocurement.gov.tl/' },
                    { label: 'Register as a supplier', href: 'http://www.eprocurement.gov.tl/' },
                    { label: 'Track an invoice', to: '/budgetspendingpage' },
                ],
            },
            {
                title: 'Rules and guidance',
                links: [
                    { label: 'Procurement legislation', to: '/alltreasurydocuments' },
                    { label: 'Guidance and publications', to: '/publications' },
                    { label: 'Knowledge centre', to: '/allknowledge' },
                ],
            },
        ],
    },
    {
        id: 'economy',
        label: 'Economy & aid',
        to: '/economypage',
        description:
            'Macroeconomic policy, national statistics and the coordination of development assistance.',
        columns: [
            {
                title: 'Economy',
                links: [
                    { label: 'Economy overview', to: '/economypage' },
                    { label: 'Research and analysis', to: '/research' },
                    { label: 'National statistics', href: 'https://inetl-ip.gov.tl' },
                ],
            },
            {
                title: 'Development aid',
                links: [
                    { label: 'Aid and external resources', to: '/aidpage' },
                    { label: 'Development partners', to: '/partners' },
                    { label: 'Programmes', to: '/programs' },
                ],
            },
            {
                title: 'Data and reports',
                links: [
                    { label: 'Publications and reports', to: '/publications' },
                    { label: 'Budget and revenue by year', to: '/budgetandrevenueallyears' },
                    { label: 'Infographics and posters', to: '/allpostergraphics' },
                ],
            },
        ],
    },
    {
        id: 'news',
        label: 'News',
        to: '/latestnews',
    },
    {
        id: 'publications',
        label: 'Publications',
        to: '/publications',
    },
    {
        id: 'contact',
        label: 'Contact',
        to: '/contact',
    },
];

/** Related national systems, surfaced in the utility bar and the footer. */
export const relatedSites = [
    { label: 'Tax Authority', href: 'https://attl.gov.tl/' },
    { label: 'Customs', href: 'https://customs.gov.tl/' },
    { label: 'e-Procurement', href: 'http://www.eprocurement.gov.tl/' },
    { label: 'Statistics (INETL)', href: 'https://inetl-ip.gov.tl' },
];

export const languages = [
    { code: 'en', label: 'English', short: 'ENG' },
    { code: 'tet', label: 'Tetum', short: 'TET' },
    { code: 'pt', label: 'Português', short: 'POR' },
];
